import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../utils/axiosInstance";

// Load saved preferences from backend
export const loadTablePreferences = createAsyncThunk(
  "tablePreferences/load",
  async ({ tableId, defaultColumns }, { rejectWithValue,  getState }) => {
    try {
        const {auth} = getState();
      const res = await axiosInstance.get(`/api/table-preferences/${tableId}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      let prefs = res.data?.preferences;

      // Normalize field names - fix outdated field names
      if (prefs && Array.isArray(prefs)) {
        prefs = prefs.map(col => {
          if (col.id === 'amount' && col.field === 'amount') {
            return { ...col, field: 'totalAmount' };
          }
          return col;
        });
      }

      return {
        tableId,
        preferences: prefs && prefs.length > 0 ? prefs : (defaultColumns || []),
      };
    } catch (err) {
      console.warn("Failed to load table preferences, using defaults:", err.message);
      // Return default columns if loading fails
      return {
        tableId,
        preferences: defaultColumns || [],
      };
    }
  }
);

// Save preferences to backend
export const saveTablePreferences = createAsyncThunk(
  "tablePreferences/save",
  async ({ tableId, preferences }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      
      const response = await axiosInstance.post(
        "/api/table-preferences",
        { tableName: tableId, preferences },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      
      return { tableId, preferences };
    } catch (err) {
      console.error("saveTablePreferences: Error", {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const tablePreferenceSlice = createSlice({
  name: "tablePreference",
  initialState: {
    tables: {},
  },
  reducers: {
    updateLocalPreferences: (state, action) => {
      const { tableId, preferences } = action.payload;

      if (!state.tables[tableId]) {
        state.tables[tableId] = { columns: [], loading: false, error: null };
      }
      state.tables[tableId].columns = preferences;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTablePreferences.pending, (state, action) => {
        const id = action.meta.arg.tableId;
        if (!state.tables[id]) {
          state.tables[id] = { columns: [], loading: false, error: null };
        }
        state.tables[id].loading = true;
      })
      .addCase(loadTablePreferences.fulfilled, (state, action) => {
        const { tableId, preferences } = action.payload;
        state.tables[tableId].loading = false;
        state.tables[tableId].columns = preferences;
      })
      .addCase(loadTablePreferences.rejected, (state, action) => {
        const id = action.meta.arg.tableId;
        state.tables[id].loading = false;
        state.tables[id].error = action.payload;
      })

      .addCase(saveTablePreferences.fulfilled, (state, action) => {
        const { tableId, preferences } = action.payload;
        if (!state.tables[tableId]) {
          state.tables[tableId] = { columns: [], loading: false, error: null };
        }
        state.tables[tableId].columns = preferences;
        state.tables[tableId].error = null;
      })
      .addCase(saveTablePreferences.rejected, (state, action) => {
        const tableId = action.meta.arg.tableId;
        if (!state.tables[tableId]) {
          state.tables[tableId] = { columns: [], loading: false, error: null };
        }
        state.tables[tableId].error = action.payload;
        console.error("saveTablePreferences failed:", action.payload);
      });
  },
});

export const { updateLocalPreferences } = tablePreferenceSlice.actions;
export default tablePreferenceSlice.reducer;
